import { Button, ButtonGroup, Modal } from "@datapay-by-datacom/design-system";
import { Heading } from "react-aria-components";
import styles from "./WorkPatternImportCommitConfirmModal.module.css";

interface WorkPatternImportCommitConfirmModalProps {
  isOpen: boolean;
  /** Total patterns that will be committed (eligible rows only for partial import). */
  importableCount: number;
  /**
   * Number of blocking-error patterns that will be skipped (0 for a full import).
   * When > 0, partial-import copy is shown.
   */
  skippedCount?: number;
  /**
   * When true, eligible patterns still have unacknowledged standard-values
   * warnings — the modal explains that CSV values will be used as-is.
   */
  overrideWarnings?: boolean;
  /**
   * How many of the imported patterns still carry standard-hours/standard-days
   * warnings. The amber summary row renders only when `overrideWarnings` is true
   * and this count is greater than zero. Defaults to 0.
   */
  warningCount?: number;
  onCancel: () => void;
  onImport: () => void;
}

/**
 * Confirms commit before the work pattern import is sent (#401, #414).
 *
 * The body is an at-a-glance, colour-coded summary — the user sees what they're
 * confirming (imported / skipped / kept-warnings counts) without reading prose.
 * The title varies by scenario (in precedence order):
 *  - Partial + override warnings → "Import valid patterns and override warnings?"
 *  - Partial only              → "Import valid patterns?"
 *  - Override warnings only    → "Override warnings and import?"
 *  - Standard                  → "Confirm import?"
 */
export function WorkPatternImportCommitConfirmModal({
  isOpen,
  importableCount,
  skippedCount = 0,
  overrideWarnings = false,
  warningCount = 0,
  onCancel,
  onImport,
}: WorkPatternImportCommitConfirmModalProps) {
  const isPartial = skippedCount > 0;
  const importLabel = importableCount === 1 ? "work pattern" : "work patterns";
  const skippedLabel = skippedCount === 1 ? "pattern" : "patterns";
  const warningLabel = warningCount === 1 ? "warning" : "warnings";

  let title: string;

  if (isPartial && overrideWarnings) {
    title = "Import valid patterns and override warnings?";
  } else if (isPartial) {
    title = "Import valid patterns?";
  } else if (overrideWarnings) {
    title = "Override warnings and import?";
  } else {
    title = "Confirm import?";
  }

  return (
    <Modal isOpen={isOpen}>
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <Heading slot="title">{title}</Heading>
        </div>

        {/*
          At-a-glance outcome summary. Leads with the numbers and colour so the
          user can see exactly what they're confirming without reading prose.
          Same success/warning/danger colours as the validation report.
        */}
        <ul className={styles.summary}>
          <li className={`${styles.summaryItem} ${styles.itemSuccess}`}>
            <span className={styles.itemIcon} aria-hidden="true">
              ✓
            </span>
            <span className={styles.itemCount}>{importableCount}</span>
            <span className={styles.itemLabel}>
              {importLabel} will be imported
            </span>
          </li>

          {isPartial && (
            <li className={`${styles.summaryItem} ${styles.itemDanger}`}>
              <span className={styles.itemIcon} aria-hidden="true">
                ✕
              </span>
              <span className={styles.itemCount}>{skippedCount}</span>
              <span className={styles.itemLabel}>
                {skippedLabel} skipped — download the errors CSV, fix, and
                re-upload
              </span>
            </li>
          )}

          {overrideWarnings && warningCount > 0 && (
            <li className={`${styles.summaryItem} ${styles.itemWarning}`}>
              <span className={styles.itemIcon} aria-hidden="true">
                ⚠
              </span>
              <span className={styles.itemCount}>{warningCount}</span>
              <span className={styles.itemLabel}>
                kept standard-hours / standard-days {warningLabel} — your CSV
                values are used as-is for the import
              </span>
            </li>
          )}
        </ul>

        <div className={styles.actions}>
          <ButtonGroup>
            <Button variant="tertiary" onPress={onCancel}>
              Cancel
            </Button>
            <Button variant="primary" onPress={onImport}>
              Import
            </Button>
          </ButtonGroup>
        </div>
      </div>
    </Modal>
  );
}
